/**
 * This module defines the Delete action and its bindings to an `ItemsScope`.
 */
import * as React from 'react';
import { deleteItems as defaultDeleteItems, deleteItemsOperation } from '../../../../operations/deleteItems';
import { makeObject } from '../../../../utilities/objects';
import { withSelectedItems, IDecorateWithSelectedItemsProps } from '../../../decorators/selectors';
import { deleteActionControler, IDeleteWizardProps } from '../../../../controls/deleteActionControl';
import { WizardAction, IWizardActionStateProps } from '../../../wizardAction/WizardAction';
import { getItemActionEngagement } from '../../../../models/telemetry/getItemEngagement';
import { connectToItemCache } from '../../../decorators/connectToItemCache';
import { IItemCache } from '../../../../models/IItemCacheObserver';
import { ISelectedActionProps, ActionSelector } from '../../../withAction/WithAction';

export interface IItemDeleteActionProps {
    itemKeys: string[];
    deleteWizard: React.ComponentType<IDeleteWizardProps>;
    deleteItems?: typeof defaultDeleteItems;
}

export const ItemDeleteAction: React.ComponentType<IItemDeleteActionProps> = connectToItemCache((
    itemCache: IItemCache,
    props: IItemDeleteActionProps
): ISelectedActionProps => {
    const {
        itemKeys,
        deleteWizard,
        deleteItems = defaultDeleteItems
    } = props;

    if (itemKeys.length === 0) {
        return {
            action: null
        };
    }

    const isBatchDelete = itemKeys.length > 0;

    let isAvailable = true;

    let FinalDeleteWizard = deleteWizard;

    for (const itemKey of itemKeys) {
        const controlHandler = itemCache.demandItemFacet(deleteActionControler, itemKey);

        const {
            isAvailable: itemIsAvailable,
            deleteWizard: ItemDeleteWizard
        } = deleteActionControler.evaluate(controlHandler)(itemCache, {
            itemKey: itemKey,
            isAvailable: false,
            deleteWizard: deleteWizard,
            isBatchDelete: isBatchDelete
        });

        if (!itemIsAvailable) {
            isAvailable = false;
        }

        FinalDeleteWizard = ItemDeleteWizard;
    }

    const { dispatch } = itemCache;

    const onDelete = async () => {
        const options: (typeof deleteItemsOperation.type) = {};
        await dispatch(deleteItems({
            items: makeObject(itemKeys, () => options)
        }));
    };

    return {
        action: (
            <WizardAction
                isAvailable={ isAvailable }
                { ...getItemActionEngagement(itemCache, {
                    itemKey: itemKeys[0],
                    engagement: {
                        name: 'Delete',
                        isIntentional: true
                    }
                }) }
            >
                { (wizardProps: IWizardActionStateProps) => {
                    return (
                        <FinalDeleteWizard
                            { ...wizardProps }
                            onDelete={ onDelete }
                            itemKeys={ itemKeys }
                        />
                    );
                } }
            </WizardAction>
        )
    };
})(ActionSelector);

export type IDeleteActionProps = IDecorateWithSelectedItemsProps<IItemDeleteActionProps>;

export const DeleteAction: React.ComponentType<IDeleteActionProps> = withSelectedItems()(ItemDeleteAction);
